import type { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { Track } from 'livekit-client'

/**
 * Pure track-selection logic for the equipment stage (D-03). No React, no LiveKit context reads -
 * every function here takes tracks as plain data so it can be unit-tested without a room.
 */

export type StageRole = 'worker' | 'customer'

/** Stable identity for a track: who published it plus which source, independent of list order. */
export function trackKey(t: TrackReferenceOrPlaceholder): string {
  return `${t.participant.identity}:${t.source}`
}

export function sameTrack(a?: TrackReferenceOrPlaceholder, b?: TrackReferenceOrPlaceholder): boolean {
  if (a == null || b == null) return false
  return trackKey(a) === trackKey(b)
}

function byIdentity(a: TrackReferenceOrPlaceholder, b: TrackReferenceOrPlaceholder): number {
  return a.participant.identity.localeCompare(b.participant.identity)
}

/**
 * Picks which track goes on stage when nothing is explicitly pinned. Only camera tracks are ever
 * candidates - screen shares are never auto-selected. Sorted by identity, never by list order,
 * because useTracks can reorder entries by speaking activity (D-03: never auto-switch on speaker).
 */
export function pickDefaultStageTrack(
  tracks: TrackReferenceOrPlaceholder[],
  role: StageRole,
): TrackReferenceOrPlaceholder | undefined {
  const cameraTracks = tracks.filter((t) => t.source === Track.Source.Camera)
  const local = cameraTracks.find((t) => t.participant.isLocal)
  const remote = cameraTracks.filter((t) => !t.participant.isLocal).sort(byIdentity)

  if (role === 'worker') {
    const remoteEndusers = remote.filter((t) => t.participant.identity.startsWith('enduser-'))
    if (remoteEndusers.length > 0) return remoteEndusers[0]
    if (remote.length > 0) return remote[0]
    return local
  }

  // customer (the technician showing equipment): prefer their own camera, then the lowest-identity remote camera.
  if (local != null) return local
  if (remote.length > 0) return remote[0]
  return undefined
}
